import { CSSProperties, useCallback } from 'react';
import {
  Field,
  ImageField,
  LinkField,
  TextField,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';
import { sanitize } from 'isomorphic-dompurify';

import {
  Button,
  ButtonVariantType,
  isColorBright,
  mapSharedPropertiesToFields,
  resolveColorProperty,
} from '@gn-audio/jss-components';
import { ComponentProps } from '@gn-audio/jss-components/lib/component-props';

import { Eyebrow } from 'common/components/Eyebrow';
import { ResponsiveImage } from 'composables/shared/ResponsiveImage/ResponsiveImage';

type EnterpriseGenericPromoSpotLayout = 'middle' | 'right' | 'left' | 'bottom' | 'top';

type EnterpriseGenericPromoSpotProps = {
  fields: EnterpriseGenericPromoSpotRendering;
} & ComponentProps;

type EnterpriseGenericPromoSpotRendering = {
  eyebrowText: TextField;
  title: TextField;
  description: TextField;
  backgroundColor: TextField;
  orientation: Field<EnterpriseGenericPromoSpotLayout>;
  firstCtaButtonText: TextField;
  firstCtaButtonVariant: Field<ButtonVariantType>;
  firstCtaButtonLink: LinkField;
  secondCtaButtonText: TextField;
  secondCtaButtonVariant: Field<ButtonVariantType>;
  secondCtaButtonLink: LinkField;
  desktopImage: ImageField;
  mobileImage: ImageField;
  imageLink: LinkField;
  imageBackgroundGradient: TextField;
  fontColor: TextField;
  roundedImage: Field<boolean>;
};

const CONTAINER_CLASSES = {
  base: 'flex justify-center w-full container-fluid gap-8',
  layouts: {
    middle: '',
    right: '',
    left: '',
    bottom: 'flex-col',
    top: 'flex-col-reverse',
  },
} as const;

const LAYOUT_CLASSES = {
  base: 'container flex items-center gap-8 py-0',
  layouts: {
    middle: 'flex-col text-center',
    right: 'flex-col-reverse md:flex-row',
    left: 'flex-col-reverse md:flex-row-reverse',
    bottom: 'flex-col-reverse text-center',
    top: 'flex-col text-center',
  },
} as const;

const CONTENT_CLASSES = {
  base: 'flex flex-col gap-8',
  layouts: {
    middle: 'items-center max-w-6xl',
    right: 'w-full md:w-1/2',
    left: 'w-full md:w-1/2',
    bottom: 'items-center max-w-6xl',
    top: 'items-center max-w-6xl',
  },
} as const;

const IMAGE_CLASSES = {
  base: 'flex justify-center align-middle',
  layouts: {
    middle: 'py-6',
    right: 'w-full md:w-1/2 rounded-xl',
    left: 'w-full md:w-1/2 rounded-xl',
    bottom: 'w-full',
    top: 'w-full',
  },
} as const;

const EnterpriseGenericPromoSpot = ({ fields }: EnterpriseGenericPromoSpotProps) => {
  const {
    eyebrowText,
    desktopImage,
    mobileImage,
    title,
    description,
    backgroundColor,
    imageBackgroundGradient,
    orientation,
    firstCtaButtonText,
    secondCtaButtonText,
    firstCtaButtonVariant,
    secondCtaButtonVariant,
    firstCtaButtonLink,
    secondCtaButtonLink,
    imageLink,
    fontColor,
    roundedImage,
  } = mapSharedPropertiesToFields<EnterpriseGenericPromoSpotRendering>(fields);

  const backgroundColorHex = resolveColorProperty(backgroundColor.value as string) ?? '#ffffff';
  const fontColorHex = fontColor?.value
    ? resolveColorProperty(fontColor.value as string)
    : undefined;

  const isBackgroundColorBright = isColorBright(backgroundColorHex);

  const backgroundTextColorClass = isBackgroundColorBright ? 'light' : 'dark';

  const hasImage = desktopImage?.value?.src || mobileImage?.value?.src;

  const spotContainerStyles: CSSProperties = {
    backgroundColor: backgroundColorHex,
  };

  const imageBackgroundGradientStyle: CSSProperties = {
    background: imageBackgroundGradient.value,
  };

  const imageOrientation = !orientation.value ? 'bottom' : orientation.value;

  const commonProps = {
    style: spotContainerStyles,
    className: classNames(
      CONTAINER_CLASSES.base,
      CONTAINER_CLASSES.layouts[imageOrientation],
      backgroundTextColorClass
    ),
  };

  const renderImage = useCallback(
    () =>
      imageLink?.value.href ? (
        <a className="block focus-visible:outline-focus" href={imageLink.value.href}>
          <ResponsiveImage
            desktopImage={desktopImage}
            mobileImage={mobileImage}
            rounded={roundedImage?.value}
          />
        </a>
      ) : (
        <ResponsiveImage
          desktopImage={desktopImage}
          mobileImage={mobileImage}
          rounded={roundedImage?.value}
        />
      ),
    [imageLink, desktopImage, mobileImage, roundedImage]
  );

  return (
    <div {...commonProps}>
      <div className={classNames(LAYOUT_CLASSES.base, LAYOUT_CLASSES.layouts[imageOrientation])}>
        <div
          className={classNames(CONTENT_CLASSES.base, CONTENT_CLASSES.layouts[imageOrientation])}
        >
          <div>
            <Eyebrow
              className={classNames(
                'mb-4',
                ['left', 'right'].includes(orientation.value) &&
                  'max-md:items-center max-md:text-center'
              )}
              align={['top', 'bottom', 'middle'].includes(orientation.value) ? 'center' : 'left'}
              text={eyebrowText.value as string}
            />
            <h2
              className="heading-2 max-md:text-center"
              style={{ color: fontColorHex }}
              dangerouslySetInnerHTML={{ __html: sanitize(title?.value as string) }}
            />
            {orientation.value === 'middle' && hasImage && (
              <div
                className={classNames(IMAGE_CLASSES.base, IMAGE_CLASSES.layouts[orientation.value])}
              >
                {renderImage()}
              </div>
            )}
            <p
              className="paragraph-large max-md:text-center"
              style={{ color: fontColorHex }}
              dangerouslySetInnerHTML={{ __html: sanitize(description?.value as string) }}
            />
          </div>
          <div className="flex flex-wrap gap-4 max-md:justify-center">
            {!!firstCtaButtonLink.value?.href && (
              <Button
                variant={firstCtaButtonVariant.value || 'primary'}
                asLink
                href={firstCtaButtonLink.value.href}
                size="m"
                disabled={false}
              >
                {firstCtaButtonText.value}
              </Button>
            )}
            {!!secondCtaButtonLink.value?.href && (
              <Button
                variant={secondCtaButtonVariant.value || 'primary'}
                asLink
                href={secondCtaButtonLink.value.href}
                size="m"
                disabled={false}
              >
                {secondCtaButtonText.value}
              </Button>
            )}
          </div>
        </div>
        {hasImage && !['top', 'bottom', 'middle'].includes(imageOrientation) && (
          <div className={classNames(IMAGE_CLASSES.base, IMAGE_CLASSES.layouts[imageOrientation])}>
            {renderImage()}
          </div>
        )}
      </div>
      {hasImage && ['top', 'bottom'].includes(imageOrientation) && (
        <div style={imageBackgroundGradientStyle}>
          <div className={classNames(IMAGE_CLASSES.base, IMAGE_CLASSES.layouts[imageOrientation])}>
            {renderImage()}
          </div>
        </div>
      )}
    </div>
  );
};

export default withDatasourceCheck()(EnterpriseGenericPromoSpot);
